import React, { useRef, useState } from 'react';
import { Alert, Box, Button } from '@mui/material';
import { Uniform } from '../../../../components/uniform/uniform';
import { UniformStep } from '../../../../components/uniform/uniform-step';
import { UniformStepContainer } from '../../../../components/uniform/uniform-step-container';
import { Accordion, AccordionSummary, AccordionDetails } from '../../../../components/accordion';
import { CitizenAttendanceAccordion } from '../components/citizen-attendance-accordion';
import { CitizenAttendanceDto } from '../../../../api-service/clients';
import { DataGridPro, GridSortModel } from '@mui/x-data-grid-pro';
import { FasitDataGridToolbar } from '../../../../components/data-grid/fasit-data-grid-toolbar';
import { AttendanceImportTitlezone } from './attendance-import-titlezone';

export interface AttendanceImportComponentProps {
  citizens: CitizenAttendanceDto[];
  parseError: string | null;
  submitError: string | null;
  isSubmitting: boolean;
  onFileSelected: (file: File) => void;
  onApprove: () => void;
}

export function AttendanceImportComponent(props: AttendanceImportComponentProps) {
  const { citizens, parseError, submitError, isSubmitting, onFileSelected, onApprove } = props;
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'citizenName', sort: 'asc' }]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canApprove = citizens.length > 0 && !parseError && !isSubmitting;

  function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    // Reset so selecting the same file again after fixing it still fires onChange
    event.target.value = '';
    if (file) {
      onFileSelected(file);
    }
  }

  return (
    <Uniform
      titleZone={<AttendanceImportTitlezone />}
      onOk={onApprove}
      onCancel={() => window.close()}
      okButtonIsLoading={isSubmitting}
      showSpinnerOnNextAndComplete={isSubmitting}
    >
      <UniformStep stepName="Indlæs fremmøde" disableButtons={canApprove ? [] : ['next']}>
        <UniformStepContainer>
          <Accordion defaultExpanded>
            <AccordionSummary>Fremmøde</AccordionSummary>
            <AccordionDetails>
              <DataGridPro<CitizenAttendanceDto>
                rows={citizens}
                columns={columns}
                getRowId={x => `${x.cpr}-${x.courseNumber}`}
                autoHeight
                hideFooter
                disableColumnMenu
                disableColumnSelector
                disableSelectionOnClick
                components={{
                  Toolbar: AttendanceImportToolbar,
                }}
                sortModel={sortModel}
                onSortModelChange={model => setSortModel(model)}
                density="compact"
                getDetailPanelContent={params => <CitizenAttendanceAccordion key={`${params.row.cpr}`} citizen={params.row} />}
                getDetailPanelHeight={() => 'auto'}
              />

              <Box display="flex" justifyContent="flex-end" sx={{ mt: 2 }}>
                <input ref={fileInputRef} type="file" accept=".csv" style={{ display: 'none' }} onChange={handleFileChange} />
                <Button variant="contained" disabled={isSubmitting} onClick={() => fileInputRef.current?.click()}>
                  Indlæs fil fra LUDUS
                </Button>
              </Box>

              {parseError && (
                <Alert severity="error" sx={{ mt: 2, whiteSpace: 'pre-line' }}>
                  {parseError}
                </Alert>
              )}

              {submitError && (
                <Alert severity="error" sx={{ mt: 2, whiteSpace: 'pre-line' }}>
                  {submitError}
                </Alert>
              )}
            </AccordionDetails>
          </Accordion>
        </UniformStepContainer>
      </UniformStep>
    </Uniform>
  );
}

function AttendanceImportToolbar() {
  return FasitDataGridToolbar({ title: 'Fremmøde indlæst fra LUDUS' });
}

const columns = [
  { field: 'citizenName', headerName: 'Borger', width: 200 },
  { field: 'cpr', headerName: 'CPR-nummer', width: 150 },
  { field: 'totalOfferedLessons', headerName: 'Tilbudte lektioner i alt', flex: 1, type: 'number' },
  { field: 'totalAbsentLessons', headerName: 'Fraværslektioner i alt', flex: 1, type: 'number' },
  { field: 'absencePercentage', headerName: 'Fraværsprocent', flex: 1, type: 'number', valueFormatter: ({ value }: { value: number }) => `${value}%` },
];
